import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { ArrowRight, ChartBar as BarChart3, Briefcase, Building2, Compass, Sparkles } from 'lucide-react';
export default function HeroGlossary() {
  const essentialTerms = [{
    term: "RevPAR",
    definition: "Revenue per available room"
  }, {
    term: "ADR",
    definition: "Average daily rate"
  }, {
    term: "GOPPAR",
    definition: "Gross operating profit per room"
  }, {
    term: "OTA Commission",
    definition: "Channel distribution cost"
  }, {
    term: "Dynamic Pricing",
    definition: "Demand-based rate flexing"
  }, {
    term: "Length of Stay (LOS)",
    definition: "Minimum & maximum stay controls"
  }];
  const audienceSegments = [{
    icon: Building2,
    title: "Hotel owners & investors",
    description: "Understand financial returns, asset value impact, and net yield."
  }, {
    icon: Briefcase,
    title: "General managers",
    description: "Align front office, sales, and channel pricing with occupancy targets."
  }, {
    icon: BarChart3,
    title: "Revenue professionals",
    description: "Master algorithmic pricing formulas, pick-up pace, and OTA algorithms."
  }];
  return <section id="herorevenueglossary" data-nav="light" className="relative bg-background text-foreground pt-14 pb-20 md:pt-20 md:pb-28 overflow-hidden border-b border-border/60">
      {/* Decorative ambient subtle background elements */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[42rem] h-[22rem] bg-secondary/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-[-10%] w-[26rem] h-[26rem] bg-secondary/5 rounded-full blur-2xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        {/* Main Hero Header */}
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
          <Badge variant="outline" className="mb-6 px-3.5 py-1 text-xs uppercase tracking-widest border-secondary/40 bg-secondary/10 text-secondary-foreground font-semibold flex items-center gap-1.5">
            <Sparkles className="size-3 text-secondary" />
            Hospitality Knowledge Base
          </Badge>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.15] text-balance">
            Hospitality revenue terminology and core metrics, explained.
          </h1>

          <p className="mt-6 text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl text-balance">
            A clear reference guide for hotel owners, general managers, and revenue
            leaders to master pricing models, distribution channels, and performance
            formulas.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium px-7">
              <Link to="#">
                <span>Explore Revenue Services</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border hover:bg-muted/50 text-foreground font-medium px-7">
              <Link to="#">Book a Consultation</Link>
            </Button>
          </div>
        </div>

        {/* Essential Industry Terms Strip */}
        <div className="mt-16 md:mt-20">
          <div className="rounded-xl border border-border/80 bg-card p-6 md:p-8 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-6 mb-6 border-b border-border/60 gap-2">
              <div className="flex items-center gap-2.5">
                <Compass className="size-5 text-secondary shrink-0" />
                <h2 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-foreground">
                  Essential Industry Terms
                </h2>
              </div>
              <span className="text-xs text-muted-foreground">
                Curated by Sunil Singh
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-4 divide-y md:divide-y-0 md:divide-x divide-border/60">
              {essentialTerms.map((item, index) => <div key={index} data-index={index} className={`${index > 0 ? "pt-4 md:pt-0 md:pl-4" : ""} flex flex-col`}>
                  <span className="font-semibold text-sm md:text-base text-foreground">
                    {item.term}
                  </span>
                  <span className="text-xs sm:text-sm text-muted-foreground mt-1 leading-snug">
                    {item.definition}
                  </span>
                </div>)}
            </div>
          </div>
        </div>

        {/* Audience Section */}
        <div className="mt-20 md:mt-24">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
              Built for hospitality decision-makers
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base mt-2">
              Whether evaluating monthly STR reports or configuring RMS rate fences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {audienceSegments.map((segment, index) => {
            const IconComp = segment.icon;
            return <Card key={index} data-index={index} className="bg-card border-border/80 hover:border-secondary/50 hover:shadow-md transition-all duration-200">
                  <CardContent className="pt-6">
                    <div className="size-11 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary mb-5">
                      <IconComp className="size-5" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground font-serif mb-2">
                      {segment.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {segment.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </section>;
}